/**
 * `RemoteEncounterController` — the encounter-room client, the encounter analogue of
 * {@link RemoteRosterController}.
 *
 * The encounter room (`backend/src/encounterRoom.ts`) is the authority; this
 * client applies commands **optimistically** to a local {@link EncounterHistory}
 * then **reconciles** to the server's `encounter_change` broadcast
 * (last-write-wins — the roster's exact pattern). It is a **thin subclass of
 * {@link RemoteController}**, sharing the optimistic-apply + LWW-reconcile
 * machinery with the roster controller. A `useEncounter` hook adapts it into
 * React identically.
 *
 * **The handshake differs from the roster's**, and that is the one piece this
 * subclass overrides. The roster room is a single global room joined implicitly
 * on socket open. An encounter room is **per-encounter, resolved from the
 * client's first message** — there is no server `onOpen`. So this controller does
 * NOT seed itself on open: it overrides {@link sendJoin} to emit an explicit
 * `encounter_join { encounterId }` first, then awaits `encounter_welcome` before
 * it is `connected`. The `encounterId` is therefore a construction parameter; the
 * transport wiring fires {@link sendJoin} the moment the socket is open (see the
 * wiring in `useEncounter.openEncounterConnection`). It also overrides
 * {@link onWelcome} to record the joined room id the welcome echoes back.
 *
 * The flow (optimistic dispatch, LWW reconcile, server-driven undo/redo) lives in
 * {@link RemoteController}; see its docstring.
 *
 * **Actor attribution.** Unlike the CLI (a fresh short-lived connection per
 * invocation, which must attribute to a *stable* `"cli"` actor so undo works
 * across invocations), this controller holds **one long-lived connection** and
 * attributes commands/undo/redo to its own per-connection `you.id`. So its
 * server-reported `undoState` (keyed server-side by the connection's socket id)
 * is reliable — the CLI's stale-undo-flag caveat does not apply here.
 *
 * Persistence: none. A shared encounter is the server's state; this is a live
 * view. Transport is injected as a minimal {@link RemoteConnection} so this is
 * unit-testable against a fake and wired to a real `WebSocket` in `useEncounter`.
 */
import {
  EncounterHistory,
  applyEncounter,
  emptyEncounter,
  moveBoostFold,
  moveBoostRefusal,
  worldFor,
  type Cell,
  type EncounterCommand,
  type EncounterEvent,
  type EncounterState,
  type ResolvedStep,
} from "@lancer-vtt/domain";
import {
  gatherMoveCheckpoint,
  isMoveCheckpointPromptId,
  MOVE_CHECKPOINT_CONSUMERS,
  NeedsDecisionSignal,
  OVERWATCH_DECLINE_OPTION,
  scriptedDiceSource,
  type MoveCheckpointConsumer,
} from "@lancer-vtt/rules";
import {
  encode,
  ok,
  err,
  mapForcedIntent,
  resolutionSource,
  unwrapForced,
  type EncounterIntent,
  type EncounterRejectionCategory,
  type PromptRequest,
  type RequestMove,
  type WireCell,
  type WireMeasureEnd,
} from "@lancer-vtt/shared";
import {
  RemoteController,
  type RemoteConnection,
  type RemoteProtocol,
  type RemoteSnapshot,
  type RemoteServerMessage,
  type DispatchResult,
} from "../remoteController";
import { previewIfControlled, wouldFoldActivation } from "./activationPreview";
import { armedFoldOutcome, type AutoActivationNotice } from "./autoActivationNotice";
import { noteGmPresent, releaseAllHolds, releaseHold } from "./sequenceHolds";

/**
 * A resolution the server broadcast as the play-by-play witness: the recorded steps the result
 * card renders, the kind, the actor, and a monotonic `seq` the React layer keys a transient card
 * off (so an identical re-attack still re-shows). Narrowed here from the wire's opaque `unknown[]`.
 */
export type BroadcastResolution = {
  steps: ResolvedStep[];
  kind?: string;
  actorId: string;
  /**
   * Every answered reaction prompt id (`brace:<defender>`), present only when this resolution
   * completed one or more cross-actor reaction round-trips. Drives the stale-modal auto-dismiss.
   * An area attack's fan-out answers one per caught mech, so this is a LIST (D4).
   */
  promptIds?: string[];
  /**
   * The area-weapon template's anchor cell, present only for a multi-target Blast/Burst/Line/Cone
   * fan-out — narrowed here from the wire's opaque `unknown`.
   */
  areaOrigin?: Cell;
  /**
   * Where every board holds this resolution, as the room stamped it (`chained-resolution-sequence`
   * 4.4): before structure/overheat checks, and before the whole resolution. Absent: no holds.
   */
  holds?: { checks: boolean; start: boolean };
  /**
   * The **server-stamped** resolution id — the same value the
   * bridged COMMS feed entry carries. Unlike `seq`, which is minted client-side per socket and
   * therefore correlates nothing across the two sockets, this is what ties a result card to the
   * dice throw that produced it.
   */
  id: string;
  /** Monotonic counter — a fresh resolution always has a higher seq (drives card re-show). */
  seq: number;
}

/**
 * A map ping the server broadcast (the attention pulse): the board world point to flash
 * at, who sent it, and a monotonic `seq` the React layer keys a transient animation off
 * (so a *repeat* ping at the same spot still re-flashes). Ephemeral — never part of the
 * reconciled encounter state, exactly like {@link BroadcastResolution}.
 */
/** The room's hold settings (`chained-resolution-sequence` 4.4). */
export type SequenceState = { autoHold: boolean; holdNext: boolean; gmOnBoard: boolean };

export type PingEvent = {
  at: { x: number; y: number };
  by: string;
  /** Monotonic counter — a fresh ping always has a higher seq (drives the re-flash). */
  seq: number;
}

/**
 * A live presence cursor (one per other participant): the board world point that participant's
 * pointer is over, and who they are. Ephemeral — never part of the reconciled encounter state,
 * exactly like {@link PingEvent}. A cursor with no recent update simply holds its last position;
 * the server emits a clear (drops the entry) when the participant leaves the board or disconnects.
 */
export type CursorEvent = {
  at: { x: number; y: number };
  by: string;
}

/**
 * A live move-path preview (one per other participant who is mid-drag): the cell keys (`"x,y"`) the
 * footprint of the token they are dragging would sweep through if dropped now, and who they are.
 * Ephemeral — never part of the reconciled encounter state, exactly like {@link CursorEvent}. The
 * server emits a clear (drops the entry) when the participant drops/cancels the drag or disconnects.
 */
export type MovePreviewEvent = {
  cells: string[];
  by: string;
}

/** Another participant's live Measure ruler: its two ends, and who holds it. Ephemeral, like {@link MovePreviewEvent}. */
export type MeasureEvent = { from: WireMeasureEnd; to: WireMeasureEnd; by: string };

/**
 * A command rejection the server relayed to *this* socket only:
 * the human-readable `reason` and coarse `category` of why an optimistic action was refused,
 * plus a monotonic `seq` the React layer keys a transient toast off (so a *repeat* identical
 * rejection still re-shows). Ephemeral — never part of the reconciled encounter state, exactly
 * like {@link PingEvent}; the rejection carries no broadcast, so the controller snaps the phantom
 * optimistic apply back to authoritative state when it lands. Cleared on the next successful change.
 */
export type EncounterRejection = {
  /** The precise, human-readable reason the server computed at the gate. */
  reason: string;
  /**
   * The coarse class of the rejection (range / economy / permission / unresolvable / illegal), or
   * the client-only `"link"`: `useEncounter` refused the write locally because the socket is down.
   */
  category: EncounterRejectionCategory | "link";
  /** Monotonic counter — a fresh rejection always has a higher seq (drives the re-show). */
  seq: number;
}

/**
 * The **mover waiting-state**: when this client declares a `request_move` that
 * {@link predictsMoveHalt} says a checkpoint consumer would genuinely halt, we hold this until the
 * authoritative broadcast lands. Ephemeral — never part of the reconciled encounter state, exactly
 * like {@link EncounterRejection}. Doubles as the presentation-commit gate: `CombatApp.tsx`'s
 * `onLocalMove` wrapper consults {@link RemoteEncounterController.pendingMove} (via the SAME
 * synchronous call `dispatchMove` returns from) and skips the optimistic own-drag paint while it
 * names this combatant — the mover's own token then only moves once the diff-path `ingest` sees the
 * real authoritative change (a genuine remote-move animation), not before.
 */
export type PendingMove = {
  /** The combatant whose declared move is predicted to halt. */
  combatantId: string;
  /** Monotonic counter — a fresh predicted-halt always bumps the seq. */
  seq: number;
}

/**
 * Predict — PURELY, client-side, with ZERO server round-trip — whether a declared
 * `combatantId → to` move would halt the checkpoint gather RIGHT NOW. Runs the EXACT same pure
 * gather ({@link gatherMoveCheckpoint}) the server's `applyMoveIntent` runs, against the SAME
 * production consumer list ({@link MOVE_CHECKPOINT_CONSUMERS}) — never a client-side injection
 * seam of its own. The prediction reads the client's own (possibly slightly stale,
 * optimistic-like-everything-else-here) view of the encounter state, and it is what keeps the
 * mover's own token held at `from` instead of jumping the moment a real consumer's offer is
 * eligible. Mirrors `applyMoveIntent`'s own deploy/no-op skip exactly (`from === undefined ||
 * worldFor(state).equals(from, to)`). `interactive: true` is what makes an eligible offer THROW
 * instead of silently deferring — the one bit this prediction actually needs
 * (`gatherMoveCheckpoint`'s non-interactive mode never halts, which would make prediction
 * useless).
 */
// `consumers` mirrors `gatherMoveCheckpoint`'s own optional-second-arg injection shape (never a
// SEPARATE seam) — production code (this module's `dispatchMove`) always calls this with the
// default, real {@link MOVE_CHECKPOINT_CONSUMERS}; a test injects a synthetic list through the
// exact same parameter.
export function predictsMoveHalt(args: {
  state: EncounterState;
  combatantId: string;
  to: Cell;
  /** A chosen route (a Trampler's drag): its steps are the move's distance, as on the room's gather. */
  path?: readonly Cell[];
  consumers?: readonly MoveCheckpointConsumer[];
}): boolean {
  const { state, combatantId, to, path, consumers = MOVE_CHECKPOINT_CONSUMERS } = args;
  const mover = state.combatants[combatantId];
  const from = mover?.pos ?? undefined;
  if (from === undefined || (worldFor(state).equals(from, to) && path === undefined)) return false;
  try {
    gatherMoveCheckpoint(
      {
        state,
        declaredMove: { moverId: combatantId, from, to, ...(path !== undefined ? { spaces: path.length - 1 } : {}) },
        choices: undefined,
        interactive: true,
        dice: scriptedDiceSource([]),
      },
      consumers,
    );
    return false;
  } catch (thrown) {
    return thrown instanceof NeedsDecisionSignal;
  }
}

/** Re-exported for callers/tests that wired the controller's transport surface. */
export type { RemoteConnection as EncounterConnection };
/** A reconciled read of the authoritative encounter state. */
export type EncounterSnapshot = RemoteSnapshot<EncounterState>;
/** The result of an optimistic local dispatch (mirrors the roster's `DispatchResult`). */
export type EncounterDispatchResult = DispatchResult;

/** The encounter room's wire-message vocabulary (the generic switches on these). */
const ENCOUNTER_PROTOCOL: RemoteProtocol = {
  command: "encounter_command",
  undo: "encounter_undo",
  redo: "encounter_redo",
  welcome: "encounter_welcome",
  change: "encounter_change",
  rejected: "encounter_command_rejected",
};

export class RemoteEncounterController extends RemoteController<EncounterState, EncounterCommand> {
  /** Which room to join — sent in the explicit `encounter_join` handshake. */
  private readonly encounterId: string;
  /** The id of the room the server actually joined us to (echoed in the welcome). */
  private roomId: string;
  /**
   * The viewer's slot is the GM's. Set by `useEncounter` from the session. The pre-activation
   * mirrors below read it, because the GM folds an activation on every ready unit
   * (`controlsActivation`).
   */
  viewerIsGm = false;

  /**
   * The session **resolution feed**: every resolution
   * the server broadcast this connection, in order. View-only and transient — NOT the
   * durable forward log (that lives on the server's history). `/encounter` renders the
   * result card per entry; `/map` shows the latest. Capped to keep memory bounded.
   */
  private readonly resolutionLog: BroadcastResolution[] = [];
  /** Monotonic resolution counter — every broadcast resolution gets the next seq. */
  private resolutionSeq = 0;
  /** Max resolutions kept in the session feed (the log is a live view, not the archive). */
  private static readonly RESOLUTION_CAP = 200;
  /**
   * Every prompt this client currently holds unanswered, keyed by `promptId` — **never a single
   * mutable slot**. One slot silently clobbers a held `move_checkpoint:` prompt whenever a SIBLING
   * cross-actor prompt (e.g. a GM-backstop-routed Brace) lands on the same client while the
   * checkpoint is still open; the take flow's later re-submit then finds nothing to answer and the
   * declared move is lost. Insertion order is preserved (`Map` semantics), and
   * {@link prompt} deliberately returns the MOST RECENTLY ARRIVED entry — see that getter's own
   * doc for why "most recent" (not "oldest") is the one that keeps every existing consumer
   * byte-identical. Entries are removed **by id** (answering, the auto-dismiss on a matching
   * resolution, `dismissPrompt`) — never by clearing the whole map.
   */
  private readonly prompts_ = new Map<string, PromptRequest>();
  /** The most recent map ping the server broadcast (the transient pulse), or null. */
  private latestPing_: PingEvent | null = null;
  /** The room's hold settings (`encounter_sequence_state`), or `null` before the room sends them. */
  private sequence_: SequenceState | null = null;
  /** Monotonic ping counter — every broadcast ping gets the next seq (drives the re-flash). */
  private pingSeq = 0;
  /** Other participants' live cursors, keyed by their slot id — the presence-cursor stream. */
  private readonly cursors_ = new Map<string, { x: number; y: number }>();
  /** Other participants' in-progress move-path previews, keyed by their slot id — the drag-ghost stream. */
  private readonly movePreviews_ = new Map<string, string[]>();
  /** Other participants' Measure rulers, keyed by their slot id. */
  private readonly measures_ = new Map<string, { from: WireMeasureEnd; to: WireMeasureEnd }>();
  /**
   * The route each combatant's latest chosen-path move walked, off its `move_token` event
   * (`landing-zone-full-automation` Q1). A remote client animates this rather than recomputing the
   * engine's route, which a Trampler's crossing is not.
   */
  private readonly committedMovePaths_ = new Map<string, Cell[]>();
  /** The most recent command rejection the server relayed to this socket (the transient feedback), or null. */
  private rejection_: EncounterRejection | null = null;
  /** Monotonic rejection counter — every relayed rejection gets the next seq (drives the re-show). */
  private rejectionSeq = 0;
  /** The mover whose just-dispatched move is predicted to halt, or null. */
  private pendingMove_: PendingMove | null = null;
  /** Monotonic counter — every fresh predicted-halt bumps it. */
  private pendingMoveSeq = 0;
  /**
   * The mover whose declared move is currently halted at a checkpoint gather, per the SERVER's own
   * table-wide `encounter_move_paused_event` broadcast — or
   * `null`. Unlike {@link pendingMove_} (this client's own PREDICTED halt, set synchronously off our
   * own dispatch), this is the authoritative, EVERY-client signal a bystander spectator needs (the
   * decider(s) already see the prompt; the mover already sees {@link pendingMove_}'s banner — this is
   * for everyone else, so "the table" isn't watching a frozen board with no explanation).
   */
  private tablePausedMoverId_: string | null = null;
  /**
   * The unit whose activation THIS client's own gesture would have folded — a *candidate*, armed at
   * the gesture and resolved against the server's next authoritative state by
   * {@link armedFoldOutcome}. Never a claim that the fold happened: D3 lets a rejected or no-op
   * action decline to commit the activation, so nothing is acknowledged until the room says the
   * turn is genuinely open. See `./autoActivationNotice.ts` for the whole argument, including why
   * over-arming is harmless and why the explicit ACTIVATE TURN path deliberately never arms.
   */
  private armedAutoActivation_: string | null = null;
  /** The most recent auto-activation this client's own gesture landed (the transient card), or null. */
  private autoActivation_: AutoActivationNotice | null = null;
  /** Monotonic counter — every landed auto-activation gets the next seq (drives the re-show). */
  private autoActivationSeq = 0;

  constructor(
    encounterId: string,
    conn: RemoteConnection,
    initial: EncounterState = emptyEncounter(encounterId, encounterId),
  ) {
    super({
      conn,
      protocol: ENCOUNTER_PROTOCOL,
      initial: new EncounterHistory({ state: initial }),
      reseed: (state) => new EncounterHistory({ state: state }),
    });
    this.encounterId = encounterId;
    this.roomId = encounterId;
    // `authoritative` (the base's field) is already seeded from `initial.state` by
    // `super(...)` above — no separate assignment needed here.
  }

  protected notConnectedError(): string {
    return "encounter room not connected";
  }

  // ── Handshake (the per-room seam — the one override) ──────────────────────────

  /**
   * Send the explicit `encounter_join` (the per-room handshake). The transport
   * wiring calls this the moment the socket opens — there is NO join-on-open on
   * the server, so the client must name the room. A second call is harmless (the
   * server treats a repeat join on the same connection as a no-op).
   */
  override sendJoin(): void {
    // Fold the reclaim token into the per-room join: the server verifies it and binds this
    // socket to the verified slot before resolving the room. An absent/invalid token →
    // `encounter_reject` + close.
    this.conn.send(encode({ type: "encounter_join", encounterId: this.encounterId, token: this.token }));
  }

  /** Record the joined room id the welcome echoes back. */
  protected override onWelcome(msg: RemoteServerMessage): void {
    if (msg.type === "encounter_welcome") this.roomId = msg.encounterId;
  }

  /** The id of the room actually joined (echoes the welcome). */
  get joinedEncounterId(): string {
    return this.roomId;
  }

  // ── Resolution intents + prompt round-trip ────────────────────────────────────

  /**
   * Send a **resolution intent** (an attack / activation) to the authority. Unlike a
   * command, the client never resolves it — the server rolls the dice and broadcasts
   * the recorded steps (or returns a prompt if a choice is needed). `actorId` defaults
   * to our own server-assigned id. A no-op before the welcome (no identity yet).
   *
   * **The acknowledgement's widest arming site** (4.1). Every D5 intent — fire, Barrage, a
   * system/talent row, a tech attack, Overcharge, the bespoke verbs — rides this one method, so
   * arming here covers all of them at once, off the same `resolutionSource` the room's own
   * disposition reads. It is only a *candidate*: nothing is acknowledged until the server's state
   * says the turn actually opened, which is what keeps a reaction, a held-action release and a GM
   * wave-through silent without any of them needing a case here. See `./autoActivationNotice.ts`.
   */
  /**
   * A GM's intent carries `startTurn: true` (`token-command-menu` 2c, option B): the room then folds
   * `activate_unit` in front of an action or a move on any ready unit, which is what the preview
   * (`controlsActivation` with `viewerIsGm`) already promised. A player's intent goes as it is.
   */
  private withTurnFlag(intent: EncounterIntent): EncounterIntent {
    return this.viewerIsGm ? { ...intent, startTurn: true } : intent;
  }

  sendIntent(intent: EncounterIntent, actorId?: string): boolean {
    const who = actorId ?? this.actorId;
    if (who === null || who === undefined) return false;
    this.armAutoActivation(resolutionSource(intent), who);
    // Identity is server-authoritative: no `actorId` on the wire — the
    // server attributes the intent to the socket's bound slot. We still gate on having a
    // verified id (`who`) so we don't fire before the welcome.
    this.conn.send(encode({ type: "encounter_intent", intent: this.withTurnFlag(intent) }));
    return true;
  }

  /**
   * Arm the auto-activation acknowledgement for `combatantId` **if and only if** this caller's next
   * action on it would make the room fold an `activate_unit` — {@link wouldFoldActivation}, the
   * client-side mirror of `activationDisposition`'s first arm, shared by identity with every other
   * pre-activation affordance in the browser (D4). An empty/unknown source, or a unit the fold
   * would not touch, clears any stale candidate rather than leaving one lying around.
   */
  private armAutoActivation(combatantId: string, actorId: string): void {
    const c = combatantId === "" ? undefined : this.history.state.combatants[combatantId];
    this.armedAutoActivation_ = wouldFoldActivation(this.history.state, c, { actorId, isGm: this.viewerIsGm }) ? combatantId : null;
  }

  /**
   * The most recent auto-activation this client's own gesture landed, or null — the transient
   * acknowledgement's signal (4.1). **Local to the acting client by construction**: only the client
   * that made the gesture ever arms one, so a bystanding player's feedback stays the turn tracker
   * flipping and nothing else. Cleared on a fresh welcome (a new socket is a new session).
   */
  get autoActivation(): AutoActivationNotice | null {
    return this.autoActivation_;
  }

  /**
   * Dispatch `commands` as one user gesture, arming the acknowledgement when the pair is the D6
   * pre-activation lead (`activate_unit` + the real command, {@link withLeadingActivation}).
   *
   * The lead is the client emitting the activation the room would have folded for an intent, so it
   * is an **auto**-activation in exactly the sense that matters: the player pressed BOOST, not
   * ACTIVATE TURN. Armed only once the base actually sent the pair — a locally-refused lead sends
   * nothing, so there is nothing to acknowledge. An explicit `activate_unit` never comes through
   * here (the inspector header's START TURN and PASS call `dispatch`), which is what keeps
   * a deliberate press silent.
   */
  override dispatchGesture(commands: EncounterCommand[], actorId?: string): DispatchResult {
    const lead = commands.length > 1 ? commands[0] : undefined;
    const result = super.dispatchGesture(commands, actorId);
    if (result.ok && lead !== undefined && lead.type === "activate_unit") {
      this.armedAutoActivation_ = lead.combatantId;
    }
    return result;
  }

  /**
   * Intercept every `end_turn` command the browser builds and reroute it onto the
   * `request_end_turn` **intent** (`decisions/0164` D2) — the identical two-command-tiers
   * relocation {@link dispatchMove} already does for `move_token`. Overriding the base
   * {@link RemoteController.dispatch} here, rather than editing each call site, is deliberate:
   * the inspector headers (`TurnControls`) and the round header's NEXT ROUND all call `encounter.dispatch(...)`
   * (`UseEncounter.dispatch`, wired straight to `useRoomController`'s `controller.dispatch`), and
   * `CombatApp.tsx`'s own `dispatch` wrapper falls through to `encounter.dispatch(command)` for
   * everything but `move_token` — so this ONE override is the seam that covers all four browser
   * sites at once, and a sixth caller added later inherits it for free instead of needing to
   * remember it (F2 — convert a claim into a structural property).
   *
   * **Local legality is unchanged.** `applyEncounter` runs the identical ordinary-command dry run
   * `dispatch` itself would have run (turn ownership, activation phase, …) — it does NOT walk the
   * end-of-turn hazard chain, which is server-only and only reachable through `refereeEndTurn`. A
   * locally-illegal `end_turn` (out of turn, wrong actor) is refused with the same `DispatchResult`
   * as before and nothing is sent, exactly like a plain `dispatch` rejection. On local success the
   * command is **not** applied optimistically, mirroring {@link dispatchMove} — the intent may halt
   * server-side for a System Trauma / structure-loss-interception prompt, so there is no safe
   * optimistic state to show before the authoritative broadcast lands. The raw `end_turn` COMMAND
   * stays exactly as-is for CLI / AI / tests, which never call this class (D2's permanent
   * deterministic fallback leg).
   */
  override dispatch(command: EncounterCommand, actorId?: string): DispatchResult {
    if (command.type !== "end_turn") return super.dispatch(command, actorId);
    const who = actorId ?? this.actorId;
    if (who === null || who === undefined) return err(this.notConnectedError());
    const result = applyEncounter(this.history.state, { ...command, actorId: who });
    if (!result.ok) return err(result.error.message);
    this.sendIntent({ type: "request_end_turn", combatantId: command.combatantId }, who);
    return ok(undefined);
  }

  /**
   * Dispatch a PLAY-mode voluntary move: the gameplay-tier counterpart to {@link dispatch}, but
   * for movement. Sends the `request_move` **intent** on the wire instead of an
   * `encounter_command` — the room resolves it through the checkpoint pre-apply pass. `actorId`
   * defaults to our own server-assigned id, mirroring {@link dispatch}.
   *
   * **NOT a mutating optimistic apply — a PURE legality dry-run.** A checkpoint consumer can halt
   * a move for a reaction round-trip that the mover's own client is never directly told about (the
   * prompt routes to the WATCHER + GM, never the mover), so a mutating local apply here would show
   * the mover's own token as already-moved while the real move sits parked, un-applied, on the
   * authority. Instead this calls {@link applyEncounter} (the SAME pure reducer `EncounterHistory`
   * binds) against the CURRENT state **without committing it** — movement's leaf reducer reads no
   * compendium reference data, so the default empty `ApplyCtx` is correct, as with every other
   * manual-tier command. `finishTokenDrag`'s synchronous accept/snap-back branch still gets its
   * ok/err verdict, but `this.history` / {@link publish} are left untouched. The move becomes
   * visible to THIS client only when the server's authoritative `encounter_change` broadcast lands,
   * via the same LWW reconcile every other resolution intent relies on.
   *
   * **The presentation-commit gate**: {@link predictsMoveHalt} runs the SAME pure gather the
   * server's `applyMoveIntent` will, against our own current state, and — SYNCHRONOUSLY, in this
   * same call — sets/clears {@link pendingMove} to record the verdict. `CombatApp.tsx`'s
   * `onLocalMove` wrapper reads `pendingMove` right after this call returns (the same synchronous
   * tick `finishTokenDrag` already calls `onLocalMove` in) and skips the optimistic own-drag paint
   * when it names this combatant — holding the presentation at `from` until the real authoritative
   * broadcast lands, instead of jumping the mover's own token to `to` before a genuine halt's
   * round-trip even starts.
   *
   * **Pre-activation — the one place in this project where D7 genuinely bites**
   * (`projects/pre-activation-affordances.md` 2.4). Every other gesture the room auto-activates
   * rides {@link sendIntent}, which runs NO local legality check, so an ungreyed row reaches the
   * room regardless of what the client believes. This method is the exception: the dry-run below
   * returns `err` and **sends nothing**, so a movement affordance lit up pre-activation would be a
   * drag that does nothing at all. Both the dry-run and {@link predictsMoveHalt} therefore compose
   * against `previewIfControlled` (`./activationPreview`) — the client-side mirror of the room's
   * fold arm, and the SAME derivation the server's `resolutionState` builds before handing
   * `refereeMove` this very intent.
   *
   * Two consequences worth stating, because both look like bugs and are not:
   *   - `applyEncounter` is now sometimes handed the preview. The never-commit invariant is intact:
   *     this call is the **discarded** dry-run and its result is thrown away on both branches;
   *     `this.history` never sees the preview, and the wire still carries a bare `request_move`.
   *   - The halt prediction MUST use the preview too. `gatherReaction` short-circuits to
   *     `NO_REACTION` while `turn.phase !== "acting"`, so predicting against the raw idle state
   *     would answer "no halt" for every pre-activation move while the server — gathering against
   *     its own preview — genuinely halts. The presentation-commit gate would then paint the
   *     mover's token at `to` for exactly the case it exists to hold back.
   *
   * `opts.path` is a chosen route (`landing-zone-full-automation` Q1, a Trampler's drag trail): the
   * dry run, the Boost fold and the halt prediction all read it, and it rides on the intent.
   * `opts.choices` are the folded Boost's opt-in answers (the drop card, `move-family-opt-in-surface`
   * Phase 1); they ride on the intent, which the room threads onto the folded `boost` command.
   */
  dispatchMove(combatantId: string, to: Cell, opts: { actorId?: string; path?: Cell[]; choices?: Record<string, string> } = {}): DispatchResult {
    const { path, choices } = opts;
    const who = opts.actorId ?? this.actorId;
    if (who === null || who === undefined) return err(this.notConnectedError());
    const command: EncounterCommand = { type: "move_token", actorId: who, combatantId, to, ...(path !== undefined ? { path } : {}) };
    // **The D7 site.** Both reads below run against the SAME hypothetical post-activation state the
    // room's own `resolutionState` hands `refereeMove` — see this method's "Pre-activation" note.
    const preview = previewIfControlled(this.history.state, this.history.state.combatants[combatantId], { actorId: who, isGm: this.viewerIsGm });
    // A destination only a Boost reaches is dry-run against the state the folded Boost leaves, the
    // same fold `refereeMove` applies (`move-auto-boost`); a refused Boost refuses the drop.
    const fold = moveBoostFold(preview, { moverId: combatantId, to, actorId: who, ...(path !== undefined ? { path } : {}) });
    const dryRunState = fold.kind === "boost" ? fold.state : preview;
    const result = applyEncounter(dryRunState, command);
    const refusal = fold.kind === "refused" ? moveBoostRefusal(fold.blocker) : result.ok ? null : result.error.message;
    if (refusal !== null) {
      this.pendingMove_ = null;
      // Nothing goes on the wire on this branch (the D7 contract), so there is nothing to
      // acknowledge — and a stale candidate here would fire on somebody else's later change.
      this.armedAutoActivation_ = null;
      return err(refusal);
    }
    this.pendingMove_ = predictsMoveHalt({ state: dryRunState, combatantId, to, ...(path !== undefined ? { path } : {}) })
      ? { combatantId, seq: ++this.pendingMoveSeq }
      : null;
    // The drag's acknowledgement arm (4.1). `preview !== this.history.state` is exactly
    // "`previewIfControlled` previewed", i.e. the same first-arm mirror `wouldFoldActivation`
    // reports — read off the object identity already computed above rather than a second call, for
    // the same reason `wouldFoldActivation` is derived from `previewIfControlled` by identity.
    this.armedAutoActivation_ = preview === this.history.state ? null : combatantId;
    // Publish so React re-renders `MoveHoldBanner`. Under a real halt the server routes the prompt
    // to the WATCHER + GM, never the mover, so no inbound message would otherwise trigger a publish
    // on the mover's own client — the banner would stay hidden until the eventual apply/reject,
    // which is too late to be useful.
    this.publish();
    // The room folds the Boost itself (a plain `request_move` auto-Boosts).
    const intent: RequestMove = {
      type: "request_move",
      combatantId,
      to: to as WireCell,
      ...(path !== undefined ? { path: path as WireCell[] } : {}),
      ...(choices !== undefined ? { choices } : {}),
    };
    this.conn.send(encode({ type: "encounter_intent", intent: this.withTurnFlag(intent) }));
    return ok(undefined);
  }

  /** The mover whose just-dispatched move is predicted to halt, or null. Consulted
   *  synchronously right after {@link dispatchMove} returns — see that method's own doc. */
  get pendingMove(): PendingMove | null {
    return this.pendingMove_;
  }

  /** The mover whose declared move is currently halted table-wide (the server's own broadcast), or
   *  null — the bystander-spectator signal, distinct from {@link pendingMove} (this client's own
   *  predicted halt) and the decider's own `PromptModal`. */
  get tablePausedMoverId(): string | null {
    return this.tablePausedMoverId_;
  }

  /**
   * Answer the **head** pending prompt ({@link prompt} — "most recently arrived"):
   * record `optionId` under the prompt's id into a **copy** of the echoed intent's `choices`,
   * remove that entry **by id** (any OTHER held prompt, e.g. a checkpoint offer parked under a
   * sibling Brace, survives untouched), and re-send the intent. The server re-resolves with the
   * answer present → completes (or prompts again for a further choice — iterate-pure). A no-op
   * if there is no pending prompt.
   */
  answerPrompt(optionId: string): boolean {
    const p = this.prompt;
    if (!p) return false;
    return this.answerHeld_(p, optionId);
  }

  /**
   * Answer a **specific held prompt by id**, never re-reading the head: the
   * take flow captures a checkpoint prompt's id (and `intent`) into a LOCAL the moment it's
   * offered, arms the sibling Overwatch shot, and — once that shot resolves — must re-submit the
   * ORIGINAL checkpoint prompt's recorded answer even though a nested Brace prompt has since
   * become the head (`get prompt()`'s "most recent" contract). Re-reading `this.prompt` at that
   * point would answer the WRONG prompt; this method looks the entry up by id via {@link
   * promptFor} instead. Mirrors `packages/ai/src/promptPolicy.ts`'s reference shape: it
   * captures `const prompt = obs.prompt` into a local **before** resolving the sibling attack, then
   * re-submits from that local rather than re-reading a mutable "current prompt" — the two clients
   * are meant to agree on this contract. Same excluded-intent-shapes / choices-merge behaviour as
   * {@link answerPrompt}; a no-op if we no longer hold a prompt with this id (e.g. dismissed,
   * already answered, or superseded).
   */
  answerPromptById(promptId: string, optionId: string): boolean {
    const p = this.promptFor(promptId);
    if (!p) return false;
    return this.answerHeld_(p, optionId);
  }

  /**
   * Answer the COMMS card for decision `promptId` (`stuck-brace`) — by id, never the head, since
   * two cards can be up at once. When this client no longer holds that prompt (already answered
   * once, withdrawn, or lost across a reconnect or a server restart) a GM asks the server to answer
   * it from its own record (`encounter_force_decision`): the server either resolves it or clears
   * the card and says why. Anyone else gets `"gone"`, and the card offers to dismiss itself.
   */
  answerDecision(promptId: string, optionId: string): "sent" | "gone" {
    if (this.promptFor(promptId) !== null) return this.answerPromptById(promptId, optionId) ? "sent" : "gone";
    if (!this.viewerIsGm) return "gone";
    this.conn.send(encode({ type: "encounter_force_decision", promptId, optionId }));
    return "sent";
  }

  /**
   * Shared answer mechanics for an already-looked-up held prompt entry `p` — the common tail of
   * {@link answerPrompt} (looks up the head) and {@link answerPromptById} (looks up by id): record
   * `optionId` under `choices[p.promptId]` on a **copy** of `p.intent`, remove `p.promptId` from
   * {@link prompts_} **by id**, publish, and re-send. Returns false for the enumerated intent
   * shapes that never carry a `choices` field.
   */
  private answerHeld_(p: PromptRequest, optionId: string): boolean {
    // A forced resolution's echo is its `gm_force` wrapper: answer inside it, re-send it wrapped.
    const base = unwrapForced(p.intent);
    // Stabilize, Overcharge, Hide, Search, Disengage, Shut Down / Boot Up /
    // Self-Destruct, the Grapple break/release/contest quartet, the pilot bridge (Dismount /
    // Eject / Mount / Reload), Jockey and shake-off, Ride / the explicit allied-DISMOUNT, Deploy
    // Drone, and the mine trio (Deploy / Detect / Detonate Mine) never raise a
    // `choose` prompt (Stabilize is deterministic; Overcharge draws
    // its self-heat automatically; Hide/Disengage/Shut Down/Boot Up/Self-Destruct just land or
    // clear a status/resource; Search, Grapple's break/contest, Jockey, and shake-off draw their
    // contest d20s non-interactively; Grapple's release is
    // an unconditional clear; Dismount/Eject/Mount are fully server-resolved off the
    // pre-declared target cell/mech; Reload just clears a resource, no dice at all; Ride draws NO
    // dice at all — a willing ally always
    // attaches; the allied-DISMOUNT is likewise fully server-resolved off the pre-declared `at`
    // cell, no dice; and for Deploy Drone and the mine trio, see the paragraph below), so a
    // pending prompt's echoed intent is never one of those (the intents without a `choices`
    // field). Narrowing them out keeps the spread type-honest (every remaining intent carries
    // `choices?`).
    //
    // **`request_jockey_menu` was REMOVED from this list, membership and all**
    // (`silent-equipment-autopick` Phase 3 step 11), and `request_disarm_mine` followed it out. The old justification — *"Jockey menu is fully deterministic once dispatched"* — was
    // true when written and is now false: Phase 3 step 9 threaded `choices`/`interactive` into the
    // Damage option's `resolveDamageCascade`, so a Jockey Menu that drops the CARRIER to 0 HP
    // halts and round-trips the carrier owner's System Trauma pick (Core p.80), with the
    // structure-loss interception window offered ahead of it. `RequestJockeyMenu` gained a real
    // `choices?` field in the same change, so it has an answer channel and belongs in the general
    // path below — leaving it here would silently no-op every `answerPrompt` the carrier's owner
    // makes on that prompt, and the jockeyed mech would sit halted with an unanswerable modal.
    // (Its `choice` field — distract/shred/damage — is unrelated to `choices` despite the names;
    // see the wire type's own doc.)
    //
    // **`request_disarm_mine` was removed from this list too** (`mine-detect-disarm` 2.2). A mine
    // detonation CAN halt and round-trip a System Trauma "which system is destroyed" pick (Core
    // p.80) to the blast victim's owner, and what decides it is the CALLER: `resolveMineDetonation`
    // prompts only when handed `choices`/`interactive`. Two of its three call sites hand them over
    // — the post-move proximity-trigger sweep (reached from `request_move`) and the failed-disarm
    // detonation — and `RequestDisarmMine` gained its `choices?` field in the same change, so it
    // answers through the general path below. The remote Detonate Mine action passes neither and
    // keeps `tables.ts`'s deterministic first-valid pick; `RequestDetonateMine` has no `choices`
    // field, so it has no answer channel and stays here. Deploy Drone, Deploy Mine and Detect Mine
    // reach no damage cascade at all.
    if (
      base.type === "request_stabilize" ||
      base.type === "request_overcharge" ||
      base.type === "request_hide" ||
      base.type === "request_disengage" ||
      base.type === "request_shutdown" ||
      base.type === "request_boot_up" ||
      base.type === "request_engage_compcon" ||
      base.type === "request_take_back_compcon" ||
      base.type === "request_self_destruct" ||
      base.type === "end_attack" ||
      base.type === "stop_barrage_as_skirmish" ||
      base.type === "request_search" ||
      base.type === "request_grapple_break" ||
      base.type === "request_grapple_release" ||
      base.type === "request_dismount" ||
      base.type === "request_eject" ||
      base.type === "request_mount" ||
      base.type === "request_jockey" ||
      base.type === "request_shake_off" ||
      base.type === "request_ride" ||
      base.type === "request_ride_dismount" ||
      base.type === "request_reload" ||
      base.type === "request_grapple_contest" ||
      base.type === "request_deploy_drone" ||
      base.type === "request_deploy_mine" ||
      base.type === "request_detect_mine" ||
      base.type === "request_detonate_mine"
      // `request_move` is DELIBERATELY NOT in this list. `RequestMove` carries a real `choices?`
      // field and its gather DOES halt for a prompt (the move-checkpoint offer), so it belongs in
      // the general `choices`-carrying path below, exactly like `request_attack`. Excluding it here
      // silently no-ops every `answerPrompt` call a watcher makes on a move-checkpoint prompt — the
      // held move never re-gathers, so it never applies OR rejects.
    ) {
      return false;
    }
    const choices = { ...(base.choices ?? {}), [p.promptId]: optionId };
    const next = mapForcedIntent(p.intent, () => ({ ...base, choices }));
    this.prompts_.delete(p.promptId);
    this.publish();
    return this.sendIntent(next, p.who);
  }

  /**
   * Answer a pending **direction** prompt — the spatial twin of
   * {@link answerPrompt}. Instead of recording an option id under `choices[promptId]`, this records
   * `cell` on a **copy** of the echoed intent, clears the prompt, and re-sends — the same
   * iterate-pure round-trip (the server re-resolves with the direction present and either
   * completes or raises a further prompt). A no-op when there is no pending prompt, the pending
   * prompt isn't a direction decision, or the echoed intent isn't one of the shapes below.
   *
   * **Two distinct wire fields, by intent shape — not a single `reactionToward` overload.**
   * `request_attack` / `request_barrage` (forced or not) record the cell as `reactionToward`
   * (`intentCarriesReactionToward` in `packages/rules`' `refereeIntent.ts` is the server-side twin
   * of that list and the compile-enforced one — keep the two in step). `request_activate` is a
   * SEPARATE carrier: its wire shape (`RequestActivate`, `shared/src/encounter-intents.ts`) has no
   * `reactionToward` slot at all — `REACTION_TOWARD_CARRIERS.request_activate` is `false` — and
   * `resolveActivate` (`packages/rules/src/encounter/resolve/activate.ts`) reads the answer off
   * `intent.toward` instead, the SAME field the CLI's `--toward` sets. Charge/Impale's
   * `move_self_toward_target { toward: "chosen" }` halt is `directionAnswerable` unconditionally
   * for `request_activate` (`refereeIntent.ts`), so this method has to close that loop or the
   * board click is a silent no-op and the prompt re-raises forever — found live
   * (`npc-features-fielded-roster` Phase 6.3): before this branch, `answerPromptDirection` fell
   * through to `false` for `request_activate` and clicking a cell did nothing.
   *
   * **`request_barrage` must stay in the `reactionToward` list.** A Barrage shot's DECLARED phase
   * fires the same `on_attack_declared` clauses a Skirmish does, so `t_hunter:1`'s Lunge (an
   * Auxiliary melee weapon fired in a Barrage — an utterly ordinary table action) raises a
   * direction prompt; if this method refuses it, the modal stays up with no way to answer it, a
   * permanent mid-encounter hang. The map-click gesture keys on `answerKind`, not on the intent
   * shape, so only this method's own branching governs.
   */
  answerPromptDirection(cell: Cell): boolean {
    const p = this.prompt;
    if (!p || p.answerKind !== "direction") return false;
    const base = unwrapForced(p.intent);
    let next: EncounterIntent;
    if (base.type === "request_attack" || base.type === "request_barrage") {
      next = mapForcedIntent(p.intent, () => ({ ...base, reactionToward: cell }));
    } else if (base.type === "request_activate") {
      next = mapForcedIntent(p.intent, () => ({ ...base, toward: cell }));
    } else {
      return false;
    }
    this.prompts_.delete(p.promptId);
    this.publish();
    return this.sendIntent(next, p.who);
  }

  /**
   * Drop a **map ping** at board world point `at` (scale-1 px) — the long-press-on-the-map
   * gesture. Fire-and-forget: it carries no identity on the wire (the server attributes it
   * to our bound slot) and never touches local state. A no-op before the welcome (no
   * verified identity yet), mirroring {@link sendIntent}.
   */
  sendPing(at: { x: number; y: number }): boolean {
    if (this.actorId === null) return false;
    this.conn.send(encode({ type: "encounter_ping", at }));
    return true;
  }

  /**
   * **Roll it** — release the held roll `key` on every board (`chained-resolution-sequence` 3.2).
   * Ephemeral like {@link sendPing}; the room gates it to the GM or `roller`'s controller and
   * re-broadcasts it, and the echo opens this client's latch too.
   */
  sendHoldRelease(key: string, roller: string): boolean {
    if (this.actorId === null) return false;
    this.conn.send(encode({ type: "encounter_sequence_release", key, roller }));
    return true;
  }

  /** The GM's **hold next** toggle (3.1): every board holds before the next resolution it plays. */
  sendHoldNext(armed: boolean): boolean {
    if (this.actorId === null) return false;
    this.conn.send(encode({ type: "encounter_sequence_hold_next", armed }));
    return true;
  }

  /** The GM's **auto-hold** toggle for this encounter (4.4), kept by the room. */
  sendAutoHold(on: boolean): boolean {
    if (this.actorId === null) return false;
    this.conn.send(encode({ type: "encounter_sequence_auto_hold", on }));
    return true;
  }

  /** The most recent map ping the server broadcast (the transient pulse), or null. */
  /** The room's hold settings: the encounter's auto-hold, the GM's armed "hold next", and whether a GM is on the board. */
  get sequence(): SequenceState | null {
    return this.sequence_;
  }

  get latestPing(): PingEvent | null {
    return this.latestPing_;
  }

  /**
   * Stream this client's live cursor to the room at board world point `at` (scale-1 px), or
   * `null` to clear it (the pointer left the board). Fire-and-forget like {@link sendPing}: it
   * carries no identity on the wire (the server attributes it to our bound slot) and never
   * touches local state. A no-op before the welcome (no verified identity yet). The caller is
   * expected to throttle the move stream.
   */
  sendCursor(at: { x: number; y: number } | null): boolean {
    if (this.actorId === null) return false;
    this.conn.send(encode({ type: "encounter_cursor", at }));
    return true;
  }

  /** The other participants' live cursors (a fresh array, newest snapshot) — the presence layer. */
  get cursors(): CursorEvent[] {
    return [...this.cursors_].map(([by, at]) => ({ by, at }));
  }

  /**
   * Stream this client's in-progress move-path preview to the room — the cell keys (`"x,y"`) the
   * footprint of the token being dragged would sweep, or `null` to clear it (drag dropped/cancelled).
   * Fire-and-forget like {@link sendCursor}: it carries no identity on the wire (the server attributes
   * it to our bound slot) and never touches local state. A no-op before the welcome (no verified
   * identity yet). The caller streams it on drag-cell-change (already coalesced — no extra throttle).
   */
  sendMovePreview(preview: { cells: string[] } | null): boolean {
    if (this.actorId === null) return false;
    this.conn.send(encode({ type: "encounter_move_preview", preview }));
    return true;
  }

  /**
   * Stream this client's Measure ruler ends to the room, or `null` to clear it. Fire-and-forget like
   * {@link sendMovePreview}; a no-op before the welcome.
   */
  sendMeasure(measure: { from: WireMeasureEnd; to: WireMeasureEnd } | null): boolean {
    if (this.actorId === null) return false;
    this.conn.send(encode({ type: "encounter_measure", measure }));
    return true;
  }

  /** The other participants' Measure rulers (a fresh array). */
  get measures(): MeasureEvent[] {
    return [...this.measures_].map(([by, m]) => ({ by, from: m.from, to: m.to }));
  }

  /** The route `combatantId`'s latest chosen-path move walked, or `undefined` if it has none on record. */
  committedMovePath(combatantId: string): Cell[] | undefined {
    return this.committedMovePaths_.get(combatantId);
  }

  /** The other participants' live drag-ghost trails (a fresh array, newest snapshot) — the preview layer. */
  get movePreviews(): MovePreviewEvent[] {
    return [...this.movePreviews_].map(([by, cells]) => ({ by, cells }));
  }

  /**
   * Dismiss the **head** pending prompt ({@link prompt}), by id. Any OTHER held prompt survives
   * untouched, exactly like {@link answerHeld_}'s by-id removal.
   *
   * **A `move_checkpoint:` prompt's ✕ must be a DECLINE, not a black hole.** The watcher's held
   * checkpoint prompt is the ONLY thing standing between the mover's parked `request_move` and it
   * re-gathering — locally dropping it with no re-submit orphans that move: it never applies,
   * `encounter_move_paused_event` never clears, and the table-wide banner never lifts. So a
   * checkpoint dismiss re-submits {@link
   * OVERWATCH_DECLINE_OPTION} through the same {@link answerHeld_} path {@link answerPrompt} uses
   * — the parked move then re-gathers with this watcher's offer declined, same as an explicit
   * Decline click. Every OTHER prompt kind keeps today's local-only dismiss (a Brace/direction/etc.
   * prompt has no parked side-effect waiting on it — silently backing out is the correct, existing
   * behaviour there).
   */
  dismissPrompt(): void {
    const p = this.prompt;
    if (p === null) return;
    if (isMoveCheckpointPromptId(p.promptId)) {
      this.answerHeld_(p, OVERWATCH_DECLINE_OPTION);
      return;
    }
    this.prompts_.delete(p.promptId);
    this.publish();
  }

  /**
   * The MOST RECENTLY ARRIVED pending prompt this client holds, or null if none — the "head" of
   * {@link prompts_}. Deliberately "most recent," not "oldest": that is what every consumer
   * (`PromptModal`, `DecisionCard`, the suppression gate, the e2e flows, `browserMatchHandle`'s
   * fresh-prompt watermark check) expects. To reach a held-but-not-head prompt — e.g. the take
   * flow's parked checkpoint offer — use {@link promptFor}. `Map` preserves insertion order for
   * keys that haven't been re-deleted-and-re-added, so the last entry in iteration order is the
   * most recent.
   */
  get prompt(): PromptRequest | null {
    let last: PromptRequest | null = null;
    for (const p of this.prompts_.values()) last = p;
    return last;
  }

  /**
   * A specific held prompt by id, or null if we hold no prompt with that id — the take flow's
   * seam: it needs to re-read the SPECIFIC checkpoint prompt it parked, not whichever prompt
   * happens to be {@link prompt} (the head) by the time the sibling attack resolves.
   */
  promptFor(promptId: string): PromptRequest | null {
    return this.prompts_.get(promptId) ?? null;
  }

  /** The session resolution feed (oldest → newest); a fresh copy so callers can't mutate it. */
  get resolutions(): BroadcastResolution[] {
    return this.resolutionLog.slice();
  }

  /** The most recent broadcast resolution (for the transient `/map` card), or null. */
  get latestResolution(): BroadcastResolution | null {
    return this.resolutionLog.length > 0 ? this.resolutionLog[this.resolutionLog.length - 1]! : null;
  }

  /**
   * The most recent command rejection the server relayed to this socket (reason + category),
   * or null. Set when an `encounter_command_rejected` lands (after the phantom optimistic apply
   * is reverted), cleared on the next successful `encounter_change`. The UI renders a transient,
   * categorized toast off it.
   */
  get rejection(): EncounterRejection | null {
    return this.rejection_;
  }

  // ── Inbound message routing (the prompt + resolution seams) ───────────────────

  /**
   * Intercept the two encounter-specific server messages the generic base does not
   * know — `encounter_prompt` and the `resolution` rider on `encounter_change`
   * — then delegate everything else (welcome/change/undo_state) to the base.
   * The base's `encounter_change` handler still runs (via `super.handle`) to reconcile
   * state; we only *also* capture the resolution rider for the result-card feed.
   */
  override handle(msg: RemoteServerMessage): void {
    if (msg.type === "encounter_command_rejected") {
      // The server rejected an optimistic command and broadcast NOTHING (this message is
      // originating-socket-only). Surface the categorized reason FIRST (so it's visible in the
      // snapshot `super.handle` is about to publish), then delegate the state reconcile to the
      // base's `rejected` branch: it does the precise drop-from-queue-by-watermark + rebase +
      // publish + flush — the same mechanism the `encounter_change` path uses. **Never snap the
      // whole state back** (`this.history.state = this.authoritative`); that discards every
      // unacked apply, not just the rejected one. A fresh seq so a *repeat* identical rejection
      // still re-shows downstream.
      //
      // **Except when this is a follow-on inside an open gesture** (3.2): the two-command
      // pre-activation Boost sends `activate_unit` then `boost`, and a lost race rejects both —
      // the second reason (*"boost: … is not the active actor"*) merely restates the consequence
      // of the first (*"X is mid-turn; end its turn before activating Y"*), which is the cause the
      // player needs. `coalesceRejection` says which one this is; outside a gesture it is always
      // `false` and latest-wins is untouched. Note the ORDER: this must run before `super.handle`
      // advances the watermark and closes the window.
      if (!this.coalesceRejection(msg.watermark)) {
        this.rejection_ = { reason: msg.reason, category: msg.category, seq: ++this.rejectionSeq };
      }
      // A rejected move (a fresh re-validation reject, or an ordinary illegal move) never
      // applies — clear the predicted-halt wait.
      this.pendingMove_ = null;
      // …and a rejected gesture folded no activation (D3), so its acknowledgement candidate dies
      // with it. Dropping it here is what stops a lost race from acknowledging a turn that never
      // opened, and stops a stale candidate outliving the gesture that armed it.
      this.armedAutoActivation_ = null;
      super.handle(msg);
      return;
    }
    if (msg.type === "encounter_prompt") {
      // Held **by id**, never a single mutable slot — a second, genuinely DIFFERENT
      // cross-actor prompt (e.g. a sibling Brace backstop-routed to the same GM socket while a
      // watcher's `move_checkpoint:` offer is still held) must NOT destroy the first. See
      // {@link prompts_}'s own doc for the mechanism this closes.
      this.prompts_.set(msg.prompt.promptId, msg.prompt);
      this.publish();
      return;
    }
    if (msg.type === "encounter_prompt_withdrawn") {
      // The server retired these decisions (their resolution can no longer go through); drop any
      // copy still held so no window keeps asking.
      for (const id of msg.promptIds) this.prompts_.delete(id);
      this.publish();
      return;
    }
    if (msg.type === "encounter_sequence_release_event") {
      // A held roll's release: ephemeral, never state. Open the latch the board's hold beat waits on.
      releaseHold(msg.key);
      return;
    }
    if (msg.type === "encounter_sequence_state") {
      // The room's hold settings (4.4). With no GM on the board nobody can see a hold's banner to
      // release it, so this client's holds open, and open on arrival, until a GM is back.
      this.sequence_ = { autoHold: msg.autoHold, holdNext: msg.holdNext, gmOnBoard: msg.gmOnBoard };
      if (msg.gmOnBoard) noteGmPresent();
      else releaseAllHolds();
      this.publish();
      return;
    }
    if (msg.type === "encounter_ping_event") {
      // A map ping is ephemeral — it never touches encounter state, so it does NOT go
      // through the base reconcile. Stamp a fresh seq (so a repeat ping at the same spot
      // re-flashes) and publish; the React layer re-renders and the stage animates it.
      this.latestPing_ = { at: msg.at, by: msg.by, seq: ++this.pingSeq };
      this.publish();
      return;
    }
    if (msg.type === "encounter_cursor_event") {
      // A live cursor is ephemeral — it never touches encounter state. Update (or, on a
      // `null` clear / disconnect, drop) the sender's entry and publish; the stage re-renders
      // the presence layer. The server excludes our own cursor, so the map holds only others'.
      if (msg.at === null) this.cursors_.delete(msg.by);
      else this.cursors_.set(msg.by, msg.at);
      this.publish();
      return;
    }
    if (msg.type === "encounter_move_preview_event") {
      // A move-path preview is ephemeral — it never touches encounter state. Update (or, on a
      // `null` clear / drop / disconnect, drop) the sender's trail and publish; the stage re-renders
      // the preview layer. The server excludes our own preview, so the map holds only others'.
      if (msg.preview === null) this.movePreviews_.delete(msg.by);
      else this.movePreviews_.set(msg.by, msg.preview.cells);
      this.publish();
      return;
    }
    if (msg.type === "encounter_measure_event") {
      // A Measure ruler is ephemeral, like the move preview: upsert or drop the sender's and publish.
      if (msg.measure === null) this.measures_.delete(msg.by);
      else this.measures_.set(msg.by, msg.measure);
      this.publish();
      return;
    }
    if (msg.type === "encounter_move_paused_event") {
      // The table-wide pause notice — ephemeral, server-originated (no per-sender identity,
      // unlike cursor/preview), sent to EVERY client. Just mirror the server's verdict and publish.
      this.tablePausedMoverId_ = msg.combatantId;
      this.publish();
      return;
    }
    if (msg.type === "encounter_change" && msg.resolution) {
      const r = msg.resolution;
      // Auto-dismiss: a completed cross-actor reaction round-trip rides its answered `promptIds`.
      // For EACH id this client still holds unanswered (the OTHER recipient — defender ↔ GM —
      // answered first), remove JUST that entry — any OTHER held prompt (e.g. a parked
      // `move_checkpoint:` offer this same resolution's Brace round-trip nested under) must
      // survive untouched. `Map.delete`
      // is already a no-op when the id isn't held, so no existence check is needed first.
      // Several ids at once is the area fan-out's normal case (D4): a GM backstop holding the
      // Brace copies of both caught mechs must drop both, not just the first.
      for (const id of r.promptIds ?? []) this.prompts_.delete(id);
      this.resolutionLog.push({
        steps: r.steps as ResolvedStep[],
        kind: r.kind,
        actorId: r.actorId,
        id: r.id,
        promptIds: r.promptIds,
        areaOrigin: r.areaOrigin as Cell | undefined,
        ...(r.holds !== undefined ? { holds: r.holds } : {}),
        seq: ++this.resolutionSeq,
      });
      if (this.resolutionLog.length > RemoteEncounterController.RESOLUTION_CAP) {
        this.resolutionLog.shift();
      }
      // Fall through to the base so it reconciles state + republishes (one publish).
    }
    // Clear any stale rejection on a successful reconcile — runs for the welcome seed
    // and every change (with or without a resolution rider). The base (`super.handle`
    // below) is what tracks `authoritative`.
    if (msg.type === "encounter_welcome") {
      this.rejection_ = null;
      // A fresh welcome is a fresh socket and a fresh session — the old one's candidate and its
      // landed acknowledgement both belong to a connection that no longer exists.
      this.armedAutoActivation_ = null;
      this.autoActivation_ = null;
    }
    if (msg.type === "encounter_change") {
      this.rejection_ = null;
      for (const e of msg.events as EncounterEvent[]) {
        if (e.combatantId !== null && e.path !== undefined) this.committedMovePaths_.set(e.combatantId, e.path);
      }
      // **The acknowledgement's commit point** (4.1). Resolve the armed candidate against the
      // server's own authoritative state — `msg.state` is the exact value `super.handle` is about
      // to adopt — and set the signal BEFORE delegating, so the snapshot that call publishes
      // already carries it (the same ordering the rejection relay above uses). `hold` is what keeps
      // the candidate alive across an unrelated change landing first; see
      // `./autoActivationNotice.ts` for why all three arms are load-bearing.
      const armed = this.armedAutoActivation_;
      if (armed !== null) {
        const outcome = armedFoldOutcome(msg.state as EncounterState, armed);
        if (outcome === "fire") {
          this.autoActivation_ = { combatantId: armed, seq: ++this.autoActivationSeq };
        }
        if (outcome !== "hold") this.armedAutoActivation_ = null;
      }
      // Any authoritative change ends a predicted-halt wait — either it's THIS move finally
      // landing (the common, fast case) or something else moved the world along; either way
      // there's nothing left to hold the presentation gate open for (`pendingMove_`'s only job
      // is suppressing the optimistic paint BEFORE the first change arrives).
      this.pendingMove_ = null;
    }
    super.handle(msg);
  }
}
